import { useState, useMemo, useCallback } from "react";

export const usePagination = (data, defaultItemsPerPage = 200) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);

  // Calculate total pages
  const totalPages = useMemo(() => {
    if (!data || data.length === 0) return 0;
    return Math.ceil(data.length / itemsPerPage);
  }, [data?.length, itemsPerPage]);

  // Calculate current page data
  const paginatedData = useMemo(() => {
    if (!data || data.length === 0) return [];

    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return data.slice(startIndex, endIndex);
  }, [data, currentPage, itemsPerPage]);

  // Navigation functions
  const goToPage = useCallback(
    (page) => {
      const pageNumber = Math.max(1, Math.min(page, totalPages));
      setCurrentPage(pageNumber);
    },
    [totalPages]
  );

  const goToNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  }, [currentPage, totalPages]);

  const goToPreviousPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  }, [currentPage]);

  const goToFirstPage = useCallback(() => {
    setCurrentPage(1);
  }, []);

  const goToLastPage = useCallback(() => {
    setCurrentPage(totalPages);
  }, [totalPages]);

  // Change items per page
  const changeItemsPerPage = useCallback((newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    // Reset to first page when changing items per page
    setCurrentPage(1);
  }, []);

  // Reset pagination when data changes significantly
  const resetPagination = useCallback(() => {
    setCurrentPage(1);
  }, []);

  // Calculate pagination info
  const paginationInfo = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        startItem: 0,
        endItem: 0,
        totalItems: 0,
        hasNextPage: false,
        hasPreviousPage: false,
      };
    }

    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, data.length);

    return {
      startItem,
      endItem,
      totalItems: data.length,
      hasNextPage: currentPage < totalPages,
      hasPreviousPage: currentPage > 1,
    };
  }, [data?.length, currentPage, itemsPerPage, totalPages]);

  return {
    currentPage,
    itemsPerPage,
    totalPages,
    paginatedData,
    paginationInfo,
    goToPage,
    goToNextPage,
    goToPreviousPage,
    goToFirstPage,
    goToLastPage,
    changeItemsPerPage,
    resetPagination,
  };
};
